import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Search, X, Layers, Eye, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import Pagination from '../../components/Pagination';
import ErrorPage from '../../components/ErrorPage';
import {
    getTenantTypes,
    createTenantType,
    updateTenantType,
    deleteTenantType,
    toggleTenantTypeStatus,
    clearError
} from '../../redux/masterSlice';
import useCanManage from '../../hooks/useCanManage';

const MAX_SEARCH_LENGTH = 50;
const MAX_NAME_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 200;

// Both presentational pieces live at module level so they keep a stable component
// identity — declared inside TenantType they would be a new type on every render
// and React would tear down and rebuild each row's toggle and buttons.
const StatusToggle = ({ inactive, disabled, onClick, testid }) => (
    <div
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${inactive ? 'bg-gray-300' : 'bg-[#4ABDBD]'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={() => !disabled && onClick()}
        data-testid={testid}
    >
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${inactive ? 'translate-x-1' : 'translate-x-[18px]'}`}></span>
    </div>
);

const RowActions = ({ onView, onEdit, onDelete, deleteDisabled, labels, idSuffix, prefix, canManage }) => (
    <div className="flex items-center justify-center gap-2">
        <button
            onClick={onView}
            className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105"
            title={labels.view}
            data-testid={`tenant-type-master-${prefix}-view-button-${idSuffix}`}
        >
            <Eye className="h-4 w-4" />
        </button>
        {canManage && (
        <button
            onClick={onEdit}
            className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105"
            title={labels.edit}
            data-testid={`tenant-type-master-${prefix}-edit-button-${idSuffix}`}
        >
            <Edit2 className="h-4 w-4" />
        </button>
        )}
        {canManage && (
        <button
            onClick={onDelete}
            disabled={deleteDisabled}
            className="text-red-500 hover:text-white p-2 rounded-lg bg-gradient-to-r from-red-50 to-red-50 hover:from-red-500 hover:to-red-600 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            title={labels.delete}
            data-testid={`tenant-type-master-${prefix}-delete-button-${idSuffix}`}
        >
            <Trash2 className="h-4 w-4" />
        </button>
        )}
    </div>
);

const TenantType = () => {
    const { t } = useTranslation('tenanttypemaster');
    const dispatch = useDispatch();
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('masters.manage');

    const {
        tenantTypes,
        tenantTypePagination,
        tenantTypeLoading,
        tenantTypeCreateLoading,
        tenantTypeUpdateLoading,
        tenantTypeDeleteLoading,
        tenantTypeToggleLoading,
        error
    } = useSelector((state) => state.master);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingType, setViewingType] = useState(null);
    const [editingType, setEditingType] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '' });
    const [errors, setErrors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'inactive'

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        dispatch(getTenantTypes({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery, activeTab]);

    const rows = useMemo(() => tenantTypes || [], [tenantTypes]);

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const refreshList = (page = currentPage) => {
        dispatch(getTenantTypes({
            page,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    };

    const isInactiveType = (tt) => tt?.status === false || tt?.status === 'inactive';

    const validateSearchInput = (value) => {
        if (value.length > MAX_SEARCH_LENGTH) {
            return t('tenanttypemaster.search_exceed_limit', `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters`);
        }
        if (value.trim().length === 0 && value.length > 0) {
            return t('tenanttypemaster.search_only_spaces', 'Search term cannot contain only spaces');
        }
        return '';
    };

    const handleSearchChange = (e) => {
        const sanitized = e.target.value.replace(/[<>]/g, '').slice(0, MAX_SEARCH_LENGTH);
        setSearchQuery(sanitized);
        setSearchError(validateSearchInput(sanitized));
    };

    // ==================== FORM ====================

    const handleNameChange = (e) => {
        const value = e.target.value.replace(/[<>]/g, '').slice(0, MAX_NAME_LENGTH);
        setFormData((prev) => ({ ...prev, name: value }));
        // Live validation: the field turns valid the moment it stops being blank,
        // rather than waiting for a failed save.
        setErrors((prev) => ({
            ...prev,
            name: value.trim() ? '' : t('tenanttypemaster.name_required', 'Tenant type name is required')
        }));
    };

    const handleDescriptionChange = (e) => {
        const value = e.target.value.replace(/[<>]/g, '').slice(0, MAX_DESCRIPTION_LENGTH);
        setFormData((prev) => ({ ...prev, description: value }));
        setErrors((prev) => ({ ...prev, description: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = t('tenanttypemaster.name_required', 'Tenant type name is required');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('tenanttypemaster.name_min_length', 'Tenant type name must be at least 2 characters');
        } else if (formData.name.trim().length > MAX_NAME_LENGTH) {
            newErrors.name = t('tenanttypemaster.name_max_length', `Tenant type name cannot exceed ${MAX_NAME_LENGTH} characters`);
        }
        // Description is optional, but a paste can exceed the cap that typing can't.
        if (formData.description.trim().length > MAX_DESCRIPTION_LENGTH) {
            newErrors.description = t('tenanttypemaster.description_max_length', `Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`);
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAdd = () => {
        setEditingType(null);
        setFormData({ name: '', description: '' });
        setErrors({});
        setShowModal(true);
    };

    const handleEdit = (tt) => {
        setEditingType(tt);
        setFormData({
            name: tt.name || '',
            description: tt.description || ''
        });
        setErrors({});
        setShowModal(true);
    };

    const handleView = (tt) => {
        setViewingType(tt);
        setShowViewModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingType(null);
        setFormData({ name: '', description: '' });
        setErrors({});
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        const payload = {
            name: formData.name.trim(),
            description: formData.description.trim()
        };
        try {
            if (editingType) {
                await dispatch(updateTenantType({ tenantTypeId: editingType.tenantTypeId, ...payload })).unwrap();
                toast.success(t('tenanttypemaster.updated_success', 'Tenant type has been updated successfully.'));
                refreshList();
            } else {
                await dispatch(createTenantType(payload)).unwrap();
                toast.success(t('tenanttypemaster.created_success', 'Tenant type has been created successfully.'));
                setCurrentPage(1);
                refreshList(1);
            }
            handleCloseModal();
        } catch (err) {
            toast.warn(err?.error || (editingType
                ? t('tenanttypemaster.update_failed', 'Failed to update tenant type.')
                : t('tenanttypemaster.create_failed', 'Failed to create tenant type.')));
        }
    };

    const handleDelete = async (tt) => {
        try {
            const result = await Swal.fire({
                title: t('tenanttypemaster.delete_title', 'Delete tenant type?'),
                text: t('tenanttypemaster.delete_text', { name: tt.name, defaultValue: `"${tt.name}" will be removed from the tenant type list.` }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#4ABDBD',
                confirmButtonText: t('tenanttypemaster.yes_delete', 'Yes, delete it'),
                cancelButtonText: t('tenanttypemaster.cancel', 'Cancel')
            });
            if (!result.isConfirmed) return;

            await dispatch(deleteTenantType(tt.tenantTypeId)).unwrap();
            toast.success(t('tenanttypemaster.deleted_success', 'Tenant type has been deleted successfully.'));
            refreshList();
        } catch (err) {
            // Clear the slice-level error so the page doesn't flip to ErrorPage
            // on the next render — this is a per-action failure, not a page load one.
            dispatch(clearError());
            if (err?.inUse || err?.status === 409) {
                await Swal.fire({
                    title: t('tenanttypemaster.cannot_delete_title', 'Cannot delete tenant type'),
                    text: err?.assignedCount
                        // `num`, not `count` — `count` would send i18next looking for
                        // plural-suffixed keys that this flat bundle doesn't carry.
                        ? t('tenanttypemaster.in_use_count_text', { num: err.assignedCount, defaultValue: `${err.assignedCount} tenant(s) are using this type. Move them to another type before deleting it.` })
                        : (err?.error || t('tenanttypemaster.in_use_text', 'Tenants are using this type. Move them to another type before deleting it.')),
                    icon: 'info',
                    confirmButtonColor: '#4ABDBD',
                    confirmButtonText: t('tenanttypemaster.ok', 'OK')
                });
                return;
            }
            toast.warn(err?.error || t('tenanttypemaster.delete_failed', 'Failed to delete tenant type.'));
        }
    };

    const handleStatusToggle = async (tt) => {
        const isCurrentlyInactive = isInactiveType(tt);
        const actionLabel = isCurrentlyInactive
            ? t('tenanttypemaster.activate', 'activate')
            : t('tenanttypemaster.deactivate', 'deactivate');
        try {
            const result = await Swal.fire({
                title: t('tenanttypemaster.status_change_title', 'Change status?'),
                text: t('tenanttypemaster.status_change_text', { action: actionLabel, name: tt.name, defaultValue: `Do you want to ${actionLabel} "${tt.name}"?` }),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('tenanttypemaster.confirm', 'Confirm'),
                cancelButtonText: t('tenanttypemaster.cancel', 'Cancel')
            });
            if (!result.isConfirmed) return;

            await dispatch(toggleTenantTypeStatus({ tenantTypeId: tt.tenantTypeId })).unwrap();
            toast.success(isCurrentlyInactive
                ? t('tenanttypemaster.activated_success', 'Tenant type activated.')
                : t('tenanttypemaster.deactivated_success', 'Tenant type deactivated.'));
            refreshList();
        } catch (err) {
            toast.warn(err?.error || t('tenanttypemaster.status_update_failed', 'Failed to update tenant type status.'));
        }
    };

    const actionLabels = {
        view: t('tenanttypemaster.view_details', 'View details'),
        edit: t('tenanttypemaster.edit', 'Edit'),
        delete: t('tenanttypemaster.delete', 'Delete')
    };
    const noDescription = t('tenanttypemaster.no_description', 'No description');

    // Only blank the page when the FIRST fetch fails and there is nothing to show.
    // Per-action failures are surfaced by their own toast / Swal.
    if (error && rows.length === 0 && !tenantTypeLoading) {
        return <ErrorPage error={error} pageName={t('tenanttypemaster.page_name', 'Tenant Type Management')} />;
    }

    return (
        <div className="w-full" data-testid="tenant-type-master-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-4" data-testid="tenant-type-master-header">
                    <h1 className="text-2xl sm:text-3xl pb-2 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2" data-testid="tenant-type-master-title">
                        {t('tenanttypemaster.tenant_type_management', 'Tenant Type Management')}
                    </h1>
                    {canManage && (
                    <button
                        onClick={handleAdd}
                        className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                        data-testid="tenant-type-master-add-button"
                    >
                        <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                        {t('tenanttypemaster.add_tenant_type', 'Add Tenant Type')}
                    </button>
                    )}
                </div>

                {/* Says what this master is FOR. Services used to be edited here and are
                    not any more — without this note the next reader looks for them. */}
                <div className="mb-5 rounded-xl px-3 py-2.5 text-[11.5px] flex items-start gap-2 bg-[#E7F5F4] text-[#2D8A8A]" data-testid="tenant-type-master-hint">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                        {t('tenanttypemaster.page_hint', 'A tenant type is only a classification label — it tells a patient what kind of place this is (Small Clinic, Multispecialty Hospital, Pharmacy). It does NOT decide which services the tenant gets: those are picked separately on the services tab of the tenant form.')}
                    </span>
                </div>

                {/* Tabs + search */}
                <div className="mb-5 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="tenant-type-master-tab-search-section">
                    <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2" data-testid="tenant-type-master-tabs">
                        {['active', 'inactive'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                                className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                                data-testid={`tenant-type-master-tab-${tab}`}
                            >
                                {activeTab === tab && (
                                    <motion.div
                                        layoutId="activeTenantTypeStatusTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20">
                                    {tab === 'active' ? t('tenanttypemaster.active', 'Active') : t('tenanttypemaster.inactive', 'Inactive')}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="relative group w-full sm:w-1/3" data-testid="tenant-type-master-search-container">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={22} />
                        <input
                            type="text"
                            placeholder={t('tenanttypemaster.search_placeholder', 'Search tenant types...')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={MAX_SEARCH_LENGTH}
                            className={`w-full pl-12 pr-4 py-2 bg-gray-50 border-2 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400 ${searchError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                            data-testid="tenant-type-master-search-input"
                        />
                        {searchError && (
                            <p className="text-red-600 text-xs mt-1 ml-1" data-testid="tenant-type-master-search-error">{searchError}</p>
                        )}
                    </div>
                </div>

                {/* List */}
                {tenantTypeLoading ? (
                    <div className="flex items-center justify-center py-16 sm:py-20" data-testid="tenant-type-master-loading-container">
                        <div className="flex flex-col items-center gap-4">
                            <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin"></div>
                            <p className="text-gray-500 text-sm">{t('tenanttypemaster.loading_tenant_types', 'Loading tenant types...')}</p>
                        </div>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-center py-16 sm:py-20" data-testid="tenant-type-master-empty-container">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <Layers size={48} className="text-gray-400" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2">
                            {searchQuery
                                ? t('tenanttypemaster.no_tenant_types_found', 'No tenant types found')
                                : t('tenanttypemaster.no_tenant_types_yet', 'No tenant types yet')}
                        </h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto">
                            {searchQuery
                                ? t('tenanttypemaster.try_adjusting_search', 'Try adjusting your search.')
                                : t('tenanttypemaster.create_first_tenant_type', 'Create your first tenant type so tenants can be classified for patients.')}
                        </p>
                        {canManage && !searchQuery && activeTab === 'active' && (
                            <button
                                onClick={handleAdd}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                                data-testid="tenant-type-master-empty-create-button"
                            >
                                <Plus size={20} />
                                {t('tenanttypemaster.create_first_tenant_type_button', 'Create Tenant Type')}
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-200 mb-4" data-testid="tenant-type-master-table-container">
                            <table className="w-full border-collapse" data-testid="tenant-type-master-table">
                                <thead>
                                    <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider w-16">{t('tenanttypemaster.col_sno', 'S.No')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('tenanttypemaster.col_name', 'Tenant Type')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('tenanttypemaster.col_description', 'Description')}</th>
                                        <th className="p-3 text-center text-xs font-bold uppercase tracking-wider w-28">{t('tenanttypemaster.col_status', 'Status')}</th>
                                        <th className="p-3 text-center text-xs font-bold uppercase tracking-wider w-36">{t('tenanttypemaster.col_actions', 'Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((tt, index) => {
                                        const isInactive = isInactiveType(tt);
                                        const serialNumber = ((tenantTypePagination.page || currentPage) - 1) * (tenantTypePagination.limit || itemsPerPage) + index + 1;
                                        return (
                                            <tr
                                                key={tt.tenantTypeId}
                                                className={`border-b border-gray-100/70 transition-all duration-300 ${isInactive ? 'bg-gray-100/50' : 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80'}`}
                                                data-testid={`tenant-type-master-row-${tt.tenantTypeId}`}
                                            >
                                                <td className={`p-3 text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`}>{serialNumber}</td>
                                                <td className="p-3" data-testid={`tenant-type-master-row-name-${tt.tenantTypeId}`}>
                                                    <span className="flex items-center gap-2">
                                                        <Layers className={`w-4 h-4 flex-shrink-0 ${isInactive ? 'text-gray-300' : 'text-[#4ABDBD]'}`} />
                                                        <span className={`text-[13px] font-semibold ${isInactive ? 'text-gray-400' : 'text-gray-800'}`}>{tt.name}</span>
                                                    </span>
                                                </td>
                                                <td className="p-3 max-w-[26rem]" data-testid={`tenant-type-master-row-description-${tt.tenantTypeId}`}>
                                                    {tt.description
                                                        ? <span className={`block text-xs leading-snug line-clamp-2 ${isInactive ? 'text-gray-400' : 'text-gray-600'}`} title={tt.description}>{tt.description}</span>
                                                        : <span className="text-xs text-gray-400 italic">{noDescription}</span>}
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex justify-center">
                                                        <StatusToggle
                                                            inactive={isInactive}
                                                            disabled={tenantTypeToggleLoading || !canManage}
                                                            onClick={() => handleStatusToggle(tt)}
                                                            testid={`tenant-type-master-toggle-${tt.tenantTypeId}`}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    <RowActions
                                                        prefix="row"
                                                        idSuffix={tt.tenantTypeId}
                                                        labels={actionLabels}
                                                        onView={() => handleView(tt)}
                                                        canManage={canManage}
                                                        onEdit={() => handleEdit(tt)}
                                                        onDelete={() => handleDelete(tt)}
                                                        deleteDisabled={tenantTypeDeleteLoading}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards — the same rows, stacked */}
                        <div className="sm:hidden space-y-3 mb-4" data-testid="tenant-type-master-card-list">
                            {rows.map((tt) => {
                                const isInactive = isInactiveType(tt);
                                return (
                                    <div
                                        key={tt.tenantTypeId}
                                        className={`p-4 rounded-xl shadow-md transition-all duration-300 ${isInactive ? 'bg-gray-100/50' : 'bg-gray-50 hover:shadow-lg'}`}
                                        data-testid={`tenant-type-master-card-${tt.tenantTypeId}`}
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <Layers className={`w-4 h-4 flex-shrink-0 ${isInactive ? 'text-gray-300' : 'text-[#4ABDBD]'}`} />
                                            <span className={`text-sm font-bold ${isInactive ? 'text-gray-400' : 'text-gray-800'}`} data-testid={`tenant-type-master-card-name-${tt.tenantTypeId}`}>
                                                {tt.name}
                                            </span>
                                        </div>
                                        <p className={`text-xs leading-snug mb-3 line-clamp-3 ${tt.description ? (isInactive ? 'text-gray-400' : 'text-gray-600') : 'text-gray-400 italic'}`}>
                                            {tt.description || noDescription}
                                        </p>
                                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                            <StatusToggle
                                                inactive={isInactive}
                                                disabled={tenantTypeToggleLoading || !canManage}
                                                onClick={() => handleStatusToggle(tt)}
                                                testid={`tenant-type-master-card-toggle-${tt.tenantTypeId}`}
                                            />
                                            <RowActions
                                                prefix="card"
                                                idSuffix={tt.tenantTypeId}
                                                labels={actionLabels}
                                                onView={() => handleView(tt)}
                                                canManage={canManage}
                                                onEdit={() => handleEdit(tt)}
                                                onDelete={() => handleDelete(tt)}
                                                deleteDisabled={tenantTypeDeleteLoading}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {tenantTypePagination.total > 0 && (
                            <Pagination
                                totalItems={tenantTypePagination.total}
                                currentPage={tenantTypePagination.page || currentPage}
                                itemsPerPage={tenantTypePagination.limit || itemsPerPage}
                                onPageChange={handlePageChange}
                                custombutton={rows.length === 0 ? 'disable' : ''}
                            />
                        )}
                    </>
                )}

                {/* ==================== CREATE / EDIT MODAL ==================== */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="tenant-type-master-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" data-testid="tenant-type-master-modal-content">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="tenant-type-master-modal-title">
                                    {editingType
                                        ? t('tenanttypemaster.edit_tenant_type', 'Edit Tenant Type')
                                        : t('tenanttypemaster.add_new_tenant_type', 'Add New Tenant Type')}
                                </h2>
                                <button onClick={handleCloseModal} className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer" data-testid="tenant-type-master-modal-close-button">
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="p-6 space-y-5" data-testid="tenant-type-master-form">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('tenanttypemaster.name_label', 'Tenant type name')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={handleNameChange}
                                        maxLength={MAX_NAME_LENGTH}
                                        placeholder={t('tenanttypemaster.name_placeholder', 'e.g. Multispecialty Hospital')}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.name ? 'border-red-300 bg-red-50' : formData.name.trim() ? 'border-green-300' : 'border-gray-300'}`}
                                        data-testid="tenant-type-master-name-input"
                                    />
                                    {errors.name && <p className="mt-1 text-sm text-red-500" data-testid="tenant-type-master-name-error">{errors.name}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('tenanttypemaster.description_label', 'Description')}
                                        <span className="ml-1 text-xs font-normal text-gray-400">{t('tenanttypemaster.optional', '(optional)')}</span>
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleDescriptionChange}
                                        maxLength={MAX_DESCRIPTION_LENGTH}
                                        placeholder={t('tenanttypemaster.description_placeholder', 'e.g. A single-branch clinic with a handful of consulting doctors.')}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all resize-none ${errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        data-testid="tenant-type-master-description-input"
                                    />
                                    <div className="mt-1 flex items-start justify-between gap-3">
                                        {errors.description
                                            ? <p className="text-sm text-red-500" data-testid="tenant-type-master-description-error">{errors.description}</p>
                                            : <p className="text-[11px] text-gray-500 leading-snug">{t('tenanttypemaster.description_helper', 'Shown to patients to explain what kind of place this is.')}</p>}
                                        <span className="text-[11px] text-gray-400 flex-shrink-0" data-testid="tenant-type-master-description-counter">
                                            {t('tenanttypemaster.char_count', { num: formData.description.length, max: MAX_DESCRIPTION_LENGTH, defaultValue: `${formData.description.length}/${MAX_DESCRIPTION_LENGTH}` })}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={handleCloseModal}
                                    disabled={tenantTypeCreateLoading || tenantTypeUpdateLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                    data-testid="tenant-type-master-modal-cancel-button"
                                >
                                    {t('tenanttypemaster.cancel', 'Cancel')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={tenantTypeCreateLoading || tenantTypeUpdateLoading}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                    data-testid="tenant-type-master-modal-save-button"
                                >
                                    {(tenantTypeCreateLoading || tenantTypeUpdateLoading) && (
                                        <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                    )}
                                    {tenantTypeCreateLoading || tenantTypeUpdateLoading
                                        ? (editingType ? t('tenanttypemaster.updating', 'Updating...') : t('tenanttypemaster.creating', 'Creating...'))
                                        : (editingType ? t('tenanttypemaster.update', 'Update') : t('tenanttypemaster.create', 'Create'))}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ==================== VIEW MODAL ==================== */}
                {showViewModal && viewingType && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="tenant-type-master-view-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800">{t('tenanttypemaster.tenant_type_details', 'Tenant Type Details')}</h2>
                                <button
                                    onClick={() => { setShowViewModal(false); setViewingType(null); }}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                                    data-testid="tenant-type-master-view-modal-close-button"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="p-6 space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tenanttypemaster.name_label', 'Tenant type name')}</label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-semibold">{viewingType.name}</div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tenanttypemaster.description_label', 'Description')}</label>
                                    <div className={`w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-sm whitespace-pre-wrap ${viewingType.description ? 'text-gray-800' : 'text-gray-400 italic'}`}>
                                        {viewingType.description || noDescription}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tenanttypemaster.status_label', 'Status')}</label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${!isInactiveType(viewingType) ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-200 text-gray-500 border border-gray-300'}`}>
                                            {!isInactiveType(viewingType) ? t('tenanttypemaster.active', 'Active') : t('tenanttypemaster.inactive', 'Inactive')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4">
                                <button
                                    onClick={() => { setShowViewModal(false); setViewingType(null); }}
                                    className="w-full px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium cursor-pointer"
                                >
                                    {t('tenanttypemaster.close', 'Close')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TenantType;
